import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { reportError } from "../../lib/errorReporter";

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * Phase 3B — renewal of an EXPIRED quotation.
 *
 * The expired quotation is never edited: this form describes a NEW commercial
 * draft. It opens prefilled from the source (customer, title, value, currency,
 * notes), always asks for a NEW validity date, and always proposes a NEW number
 * — the lineage lives in the database, never in the reference string.
 */
export default function RenewDialog({
  open,
  source,
  customers,
  submitting,
  error,
  onCancel,
  onConfirm,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({});
  const [refTouched, setRefTouched] = useState(false);
  const [fileMode, setFileMode] = useState("reuse");
  const fileRef = useRef(null);

  const hasSourceFile = Boolean(source?.has_file);

  useEffect(() => {
    if (!open || !source) return;
    setRefTouched(false);
    setFileMode(hasSourceFile ? "reuse" : "none");
    setForm({
      reference_number: "",
      customer_id: source.customer_id || "",
      title: source.title || "",
      value: source.value === null || source.value === undefined ? "" : String(source.value),
      currency: source.currency || "EUR",
      customer_email: source.customer_email || "",
      customer_phone: source.customer_phone || "",
      internal_notes: source.internal_notes || "",
      valid_until: todayPlus(30),
    });
    api
      .get("/quotations/next-reference")
      .then((r) => setForm((s) => ({ ...s, reference_number: r.data?.reference_number || "" })))
      .catch((err) => reportError("renewDialog.nextReference", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source?.id]);

  const minDate = useMemo(() => todayPlus(1), []);

  if (!open || !source) return null;

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      customer_id: form.customer_id,
      title: form.title,
      customer_email: form.customer_email || null,
      customer_phone: form.customer_phone || null,
      value: form.value === "" ? null : Number(form.value),
      currency: (form.currency || "EUR").toUpperCase(),
      valid_until: form.valid_until,
      internal_notes: form.internal_notes || null,
      // The suggestion is informational: unless the user edited it, the database
      // allocates the authoritative number inside the create transaction.
      reference_mode: refTouched ? "custom" : "auto",
      file_mode: fileMode === "reuse" ? "reuse" : "none",
    };
    if (refTouched) payload.reference_number = form.reference_number;
    onConfirm(payload, fileMode === "new" ? fileRef.current?.files?.[0] || null : null);
  };

  return (
    <div className="qw-modal-backdrop">
      <form
        className="qw-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="renew-title"
        data-testid="renew-dialog"
        onSubmit={submit}
        noValidate
      >
        <div className="h" id="renew-title" data-testid="renew-dialog-title">
          {t("quotations.renew_title", { reference: source.reference_number || "" })}
        </div>
        <div className="body">
          <div className="qw-grid cols-2">
            <div className="qw-field">
              <label htmlFor="renew-reference">{t("quotations.reference_label")}</label>
              <input
                id="renew-reference"
                className="qw-input"
                required
                value={form.reference_number || ""}
                onChange={(e) => {
                  setRefTouched(true);
                  upd("reference_number", e.target.value);
                }}
                data-testid="renew-reference"
              />
              {!refTouched && (
                <div className="qw-help" data-testid="renew-reference-auto-hint">
                  {t("quotations.reference_auto_hint")}
                </div>
              )}
            </div>
            <div className="qw-field">
              <label htmlFor="renew-valid-until">{t("quotations.renew_valid_until")} *</label>
              <input
                id="renew-valid-until"
                className="qw-input"
                type="date"
                required
                min={minDate}
                value={form.valid_until || ""}
                onChange={(e) => upd("valid_until", e.target.value)}
                data-testid="renew-valid-until"
              />
            </div>
            <div className="qw-field">
              <label htmlFor="renew-customer">{t("quotations.fields.customer")}</label>
              <select
                id="renew-customer"
                className="qw-select"
                required
                value={form.customer_id || ""}
                onChange={(e) => upd("customer_id", e.target.value)}
                data-testid="renew-customer"
              >
                <option value="">—</option>
                {(customers || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="qw-field">
              <label htmlFor="renew-title-field">{t("quotations.fields.title")}</label>
              <input
                id="renew-title-field"
                className="qw-input"
                required
                value={form.title || ""}
                onChange={(e) => upd("title", e.target.value)}
                data-testid="renew-title"
              />
            </div>
            <div className="qw-field">
              <label htmlFor="renew-value">{t("quotations.fields.value")}</label>
              <input
                id="renew-value"
                className="qw-input"
                type="number"
                min="0"
                step="0.01"
                value={form.value || ""}
                onChange={(e) => upd("value", e.target.value)}
                data-testid="renew-value"
              />
            </div>
            <div className="qw-field">
              <label htmlFor="renew-currency">{t("quotations.fields.currency")}</label>
              <input
                id="renew-currency"
                className="qw-input"
                maxLength={3}
                value={form.currency || ""}
                onChange={(e) => upd("currency", e.target.value.toUpperCase())}
                data-testid="renew-currency"
              />
            </div>
          </div>
          <div className="qw-field">
            <label>{t("quotations.renew_file_label")}</label>
            <div className="qw-radio-row">
              <label className="qw-radio">
                <input
                  type="radio"
                  name="renew-file-mode"
                  value="reuse"
                  checked={fileMode === "reuse"}
                  disabled={!hasSourceFile}
                  onChange={() => setFileMode("reuse")}
                  data-testid="renew-file-reuse"
                />
                <span>{t("quotations.renew_file_reuse")}</span>
              </label>
              <label className="qw-radio">
                <input
                  type="radio"
                  name="renew-file-mode"
                  value="new"
                  checked={fileMode === "new"}
                  onChange={() => setFileMode("new")}
                  data-testid="renew-file-new"
                />
                <span>{t("quotations.renew_file_new")}</span>
              </label>
              <label className="qw-radio">
                <input
                  type="radio"
                  name="renew-file-mode"
                  value="none"
                  checked={fileMode === "none"}
                  onChange={() => setFileMode("none")}
                  data-testid="renew-file-none"
                />
                <span>{t("quotations.renew_file_none")}</span>
              </label>
            </div>
            {fileMode === "new" && (
              <input
                className="qw-input"
                type="file"
                accept=".pdf,.docx,.xlsx"
                ref={fileRef}
                data-testid="renew-file-input"
              />
            )}
            {!hasSourceFile && (
              <div className="qw-help" data-testid="renew-no-source-file">
                {t("quotations.renew_source_file_missing")}
              </div>
            )}
          </div>
          <div className="qw-field">
            <label htmlFor="renew-notes">{t("quotations.fields.internal_notes")}</label>
            <textarea
              id="renew-notes"
              className="qw-textarea"
              value={form.internal_notes || ""}
              onChange={(e) => upd("internal_notes", e.target.value)}
              data-testid="renew-notes"
            />
          </div>
          {error && <div className="qw-error" data-testid="renew-error">{error}</div>}
        </div>
        <div className="footer">
          <button type="button" className="qw-btn ghost" onClick={onCancel} data-testid="renew-cancel">
            {t("common.cancel")}
          </button>
          <button type="submit" className="qw-btn primary" disabled={submitting} data-testid="renew-submit">
            {submitting ? t("common.loading") : t("quotations.renew_submit")}
          </button>
        </div>
      </form>
    </div>
  );
}
